import { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Logo } from "../../components/Logo";
import { SiteFooter } from "../../components/SiteFooter";
import { api, extractErrorMessage } from "../../lib/api";

interface InviteInfo {
  email: string;
  name: string;
  role: string;
}

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This invite link is missing its token. Use the link from your email.");
      setLoading(false);
      return;
    }
    api
      .get<InviteInfo>("/api/auth/invite-info", { params: { token } })
      .then(({ data }) => {
        setInvite(data);
        setName(data.name);
      })
      .catch((err) => setError(extractErrorMessage(err, "Invalid or expired invite link.")))
      .finally(() => setLoading(false));
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/auth/accept-invite", { token, name, password });
      setDone(true);
    } catch (err) {
      setError(extractErrorMessage(err, "Could not finish sign-up."));
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <>
        <div className="top-line" />
        <div className="page">
          <div className="card">
            <Logo />
            <h1>You&apos;re all set!</h1>
            <p className="sub">Your account is active. Sign in with your new password.</p>
            <Link to="/login" className="btn btn--primary btn--block">
              Go to Sign In
            </Link>
          </div>
        </div>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <div className="top-line" />
      <div className="page">
        <form className="card" onSubmit={onSubmit} noValidate>
          <Logo />
          <h1>Finish setting up your account</h1>
          <p className="sub">
            {invite
              ? `You've been invited as ${invite.role}. Choose a password to get started.`
              : "Choose a password to get started."}
          </p>
          {error && (
            <div className="error-msg" role="alert">
              {error}
            </div>
          )}
          {loading ? (
            <p className="sub">Checking your invite…</p>
          ) : (
            invite && (
              <>
                <div className="form-group">
                  <label htmlFor="inv-email">Email</label>
                  <input
                    id="inv-email"
                    className="form-input"
                    type="email"
                    value={invite.email}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="inv-name">Name</label>
                  <input
                    id="inv-name"
                    className="form-input"
                    type="text"
                    placeholder="Your name"
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="inv-pw">Password</label>
                  <input
                    id="inv-pw"
                    className="form-input"
                    type="password"
                    placeholder="Enter password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <p className="pw-hint">At least 8 characters</p>
                </div>
                <div className="form-group">
                  <label htmlFor="inv-pw2">Confirm Password</label>
                  <input
                    id="inv-pw2"
                    className="form-input"
                    type="password"
                    placeholder="Confirm password"
                    required
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                  />
                </div>
                <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
                  {submitting ? "Setting up..." : "Activate Account"}
                </button>
              </>
            )
          )}
          <p className="alt-link">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
      <SiteFooter />
    </>
  );
}
